/**
 * src/components/shared/DictationRecoveryBar.jsx — TICKET-324.
 *
 * The visible half of the useDictation durability contract. Renders nothing
 * until a recording fails to become text; then it says so plainly and gives
 * the three ways out: Retry, Download, Discard.
 *
 * Why it exists as a shared component: BOTH composer mics (AiDraftBlock in
 * the email modules, ChatComposerV2 in the agent chat) sat on the same hook
 * and both swallowed its errors — AiDraftBlock into one generic toast,
 * ChatComposerV2 into a console.warn nobody reads. One recovery UI, mounted
 * twice, instead of two that drift.
 *
 * Props:
 *   dictation — the object returned by useDictation() (needs `pending`,
 *               `transcribing`, `draftsUnavailable`, retry/discard/download)
 */
import { useState } from 'react';
import { RotateCw, Download, Trash2, AlertTriangle, Loader, Mic, Check } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return null;
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDuration(sec) {
  if (!sec && sec !== 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const actionBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  minHeight: 44, padding: '8px 12px', borderRadius: 10, border: 'none',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  background: 'var(--card)', color: 'var(--t1)',
  boxShadow: 'inset 0 0 0 1px var(--bdr)',
};

export default function DictationRecoveryBar({ dictation }) {
  const { t } = useT();
  const [busy, setBusy] = useState(null); // 'retry' | 'download' | null
  // TICKET-936: Discard deletes the only copy of the audio — it needs a second tap.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const pending = dictation?.pending;

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } finally { setBusy(null); }
  };

  // TICKET-936: the screen locked (or the app was hidden) mid-recording and the
  // recorder was stopped. The audio is on this device; the user decides.
  if (dictation?.interrupted) {
    return (
      <div data-testid="dictation-interrupted-bar" style={{
        marginTop: 8, padding: '10px 12px', borderRadius: 10,
        background: 'var(--warn-tint)', boxShadow: 'inset 0 0 0 1px var(--bdr)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <AlertTriangle size={16} strokeWidth={1.75} style={{ color: 'var(--warn)', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{t('dictation.interruptedTitle')}</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.4 }}>{t('dictation.interruptedHint')}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" data-testid="dictation-interrupted-continue" disabled={!!busy}
            onClick={() => run('continue', dictation.continueInterrupted)}
            style={{ ...actionBtn, background: 'var(--accent-bg)', color: 'var(--accent)', opacity: busy ? 0.5 : 1 }}>
            <Mic size={15} strokeWidth={1.75} />{t('dictation.continue')}
          </button>
          <button type="button" data-testid="dictation-interrupted-save" disabled={!!busy}
            onClick={() => run('save', dictation.stop)}
            style={{ ...actionBtn, opacity: busy ? 0.5 : 1 }}>
            <Check size={15} strokeWidth={1.75} />{t('dictation.saveWhatYouHave')}
          </button>
          <button type="button" data-testid="dictation-interrupted-discard" disabled={!!busy}
            onClick={() => {
              if (!confirmDiscard) { setConfirmDiscard(true); return; }
              setConfirmDiscard(false);
              run('discard', dictation.discardInterrupted);
            }}
            style={{ ...actionBtn, color: confirmDiscard ? 'var(--bad)' : 'var(--t2)', opacity: busy ? 0.5 : 1 }}>
            <Trash2 size={15} strokeWidth={1.75} />{confirmDiscard ? t('dictation.discardConfirm') : t('dictation.discard')}
          </button>
        </div>
      </div>
    );
  }

  // The store itself being unavailable is worth saying out loud — it means
  // the next recording is NOT crash-protected, which is a different (and
  // quieter) failure than a recording that failed to send.
  if (!pending) {
    if (!dictation?.draftsUnavailable) return null;
    return (
      <div
        data-testid="dictation-store-warning"
        style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
          padding: '8px 10px', borderRadius: 10, fontSize: 12,
          background: 'var(--warn-tint)', color: 'var(--warn)',
        }}
      >
        <AlertTriangle size={14} strokeWidth={1.75} />
        <span>{t('dictation.storeUnavailable')}</span>
      </div>
    );
  }

  const size = formatSize(pending.size_bytes);
  const dur = formatDuration(pending.duration_sec);
  const detail = [dur, size].filter(Boolean).join(' · ');
  const retrying = busy === 'retry' || dictation.transcribing;

  return (
    <div
      data-testid="dictation-recovery-bar"
      style={{
        marginTop: 8, padding: '10px 12px', borderRadius: 10,
        background: 'var(--warn-tint)', boxShadow: 'inset 0 0 0 1px var(--bdr)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <AlertTriangle size={16} strokeWidth={1.75} style={{ color: 'var(--warn)', flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
            {t('dictation.savedTitle')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.4 }}>
            {detail ? `${detail} — ` : ''}{pending.error_message || t('dictation.savedHint')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="dictation-recovery-retry"
          onClick={() => run('retry', dictation.retryPending)}
          disabled={retrying}
          style={{
            ...actionBtn,
            background: 'var(--accent-bg)', color: 'var(--accent)',
            cursor: retrying ? 'default' : 'pointer',
            opacity: retrying ? 0.5 : 1,
          }}
        >
          {retrying
            ? <Loader size={15} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />
            : <RotateCw size={15} strokeWidth={1.75} />}
          {retrying ? t('dictation.retrying') : t('dictation.retry')}
        </button>
        <button
          type="button"
          data-testid="dictation-recovery-download"
          onClick={() => run('download', dictation.downloadPending)}
          disabled={busy === 'download'}
          style={{ ...actionBtn, opacity: busy === 'download' ? 0.5 : 1 }}
        >
          <Download size={15} strokeWidth={1.75} />
          {t('dictation.download')}
        </button>
        <button
          type="button"
          data-testid="dictation-recovery-discard"
          onClick={() => {
            if (!confirmDiscard) { setConfirmDiscard(true); return; }
            setConfirmDiscard(false);
            run('discard', dictation.discardPending);
          }}
          disabled={!!busy}
          style={{ ...actionBtn, color: confirmDiscard ? 'var(--bad)' : 'var(--t2)', opacity: busy ? 0.5 : 1 }}
        >
          <Trash2 size={15} strokeWidth={1.75} />
          {confirmDiscard ? t('dictation.discardConfirm') : t('dictation.discard')}
        </button>
      </div>
    </div>
  );
}
